import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useHedera } from './useHedera';
import { useTransfer } from './useTransfer';
import { useTokenCreate } from './useTokenCreate';
import { useTokenBurn } from './useTokenBurn';
import { useScheduledTransaction } from './useScheduledTransaction';
import { useHCS } from './useHCS';
import { useTokenAssociate } from './useTokenAssociate';

// ─────────────────────────────────────────────────────────────────────────────
// Types (shared with /api/ai-agent.ts)
// ─────────────────────────────────────────────────────────────────────────────

export type AIActionType =
  | 'transfer_hbar'
  | 'create_token'
  | 'burn_tokens'
  | 'schedule_transfer'
  | 'submit_hcs_message'
  | 'associate_token'
  | 'explain_portfolio';

export interface AIAction {
  type: AIActionType;
  params: Record<string, unknown>;
  confirmationRequired: boolean;
  description: string;
}

export type MessageRole = 'user' | 'assistant' | 'system';

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  /** Pending action attached to this message (before user confirms) */
  action?: AIAction;
  /** Result of executing the action */
  actionResult?: {
    success: boolean;
    txId?: string | null;
    tokenId?: string | null;
    error?: string;
  };
  timestamp: Date;
}

export interface UseAIAgentOptions {
  /** API endpoint. Defaults to /api/ai-agent */
  apiEndpoint?: string;
}

export interface UseAIAgentResult {
  messages: ChatMessage[];
  loading: boolean;
  executing: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
  confirmAction: (messageId: string) => Promise<void>;
  cancelAction: (messageId: string) => void;
  clearChat: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Demo mode — scripted responses when no API key / demoMode
// ─────────────────────────────────────────────────────────────────────────────

const DEMO_DELAY = 1200;

// ── Send HBAR step-by-step helpers ──────────────────────────────────────────

/** Last assistant message content. */
function lastAssistantMsg(msgs: ChatMessage[]): string {
  return [...msgs].reverse().find((m) => m.role === 'assistant')?.content ?? '';
}

/** Parse a positive number from free text (e.g. "5", "0.5", "10 hbar"). */
function parseAmount(text: string): number | null {
  const m = text.match(/(\d+(?:\.\d+)?)/);
  if (m) { const n = parseFloat(m[1]); if (n > 0) return n; }
  return null;
}

/** Parse a Hedera account ID (0.0.XXXXX) from free text. */
function parseAccountId(text: string): string | null {
  const m = text.match(/\b0\.0\.(\d+)\b/);
  return m ? m[0] : null;
}

/**
 * Extract the HBAR amount we already confirmed in a prior assistant message.
 * Looks for the pattern "**X HBAR**" in assistant history.
 */
function extractConfirmedAmount(msgs: ChatMessage[]): number {
  for (const m of [...msgs].reverse()) {
    if (m.role !== 'assistant') continue;
    const match = m.content.match(/\*\*(\d+(?:\.\d+)?)\s*HBAR\*\*/i);
    if (match) return parseFloat(match[1]);
  }
  return 0;
}

// ── Main demo response ────────────────────────────────────────────────────────

function getDemoResponse(
  text: string,
  balance: number | null,
  prevMessages: ChatMessage[]
): { message: string; action?: AIAction } {
  const lower = text.toLowerCase();
  const lastAsst = lastAssistantMsg(prevMessages);

  // ── Step 3: we asked for the address → user just provided it ──
  if (lastAsst.includes('__AWAITING_ADDRESS__')) {
    const to = parseAccountId(text);
    const amount = extractConfirmedAmount(prevMessages);
    if (to && amount > 0) {
      return {
        message: `Ready to send **${amount} HBAR** to **${to}**. Confirm below to sign with your wallet.`,
        action: {
          type: 'transfer_hbar',
          params: { to, amount },
          confirmationRequired: true,
          description: `Send ${amount} HBAR → ${to}`,
        },
      };
    }
    return {
      message: 'Please enter a valid Hedera account ID — e.g. **0.0.12345**',
    };
  }

  // ── Step 2: we asked for the amount → user just typed it ──
  if (lastAsst.includes('__AWAITING_AMOUNT__')) {
    const amount = parseAmount(text);
    if (amount) {
      return {
        // Embed sentinel + the confirmed amount so step 3 can find it
        message: `Got it — **${amount} HBAR**. Now enter the recipient wallet address:\n__AWAITING_ADDRESS__`,
      };
    }
    return {
      message: 'Please enter a valid amount — e.g. **5** or **10.5**\n__AWAITING_AMOUNT__',
    };
  }

  // ── Balance ──
  if (lower.includes('balance') || (lower.includes('hbar') && lower.includes('how'))) {
    const hbar = balance?.toFixed(2) ?? '—';
    const usd = balance != null ? `~$${(balance * 0.004).toFixed(2)} USD` : '';
    return {
      message: `Your wallet holds **${hbar} HBAR**${usd ? ` (${usd})` : ''}. Would you like to do something with these funds?`,
    };
  }

  // ── Step 1: send / transfer intent ──
  if (lower.includes('send') || lower.includes('transfer')) {
    return {
      // Sentinel tells step 2 handler what we're waiting for
      message: 'How much HBAR would you like to send?\n__AWAITING_AMOUNT__',
    };
  }

  // ── NFT collection ──
  if (lower.includes('nft') || lower.includes('collection')) {
    return {
      message: 'Creating NFT collection. Please confirm:',
      action: {
        type: 'create_token',
        params: { name: 'Demo Collection', symbol: 'DEMO', type: 'NFT', maxSupply: 100 },
        confirmationRequired: true,
        description: 'Create NFT collection "Demo Collection"',
      },
    };
  }

  // ── Fungible token ──
  if (lower.includes('create') || lower.includes('token') || lower.includes('launch')) {
    return {
      message: 'Creating a new token. Please confirm:',
      action: {
        type: 'create_token',
        params: { name: 'Demo Token', symbol: 'DMT', type: 'FUNGIBLE', initialSupply: 1000, decimals: 2 },
        confirmationRequired: true,
        description: 'Create token "Demo Token" (DMT)',
      },
    };
  }

  // ── HCS message ──
  if (lower.includes('hcs') || lower.includes('topic') || lower.includes('submit') || lower.includes('log')) {
    return {
      message: 'Submitting message to the hedera-ui-kit AI Agent topic on HCS. Please confirm:',
      action: {
        type: 'submit_hcs_message',
        params: { topicId: '0.0.8359467', message: text },
        confirmationRequired: true,
        description: 'Submit to topic 0.0.8359467 (hedera-ui-kit AI Agent)',
      },
    };
  }

  // ── Staking ──
  if (lower.includes('staking') || lower.includes('stake') || lower.includes('reward')) {
    return {
      message:
        'Your account is staking on **Node 3**. Expected reward: **0.42 HBAR**. Current network APR ~6.5%. To change the node or claim rewards you need to sign a transaction via your wallet.',
    };
  }

  // ── Help ──
  if (lower.includes('what') || lower.includes('help') || lower.includes('can you')) {
    return {
      message:
        "I'm the AI assistant built into hedera-ui-kit. I can help with:\n\n• **Sending HBAR** — say: *send HBAR*\n• **Creating tokens** — say: *create a token*\n• **NFT collections** — say: *create an NFT collection*\n• **HCS messages** — say: *submit a message*\n• **Staking info** — say: *show my rewards*",
    };
  }

  return {
    message:
      'In demo mode I can simulate: sending HBAR, creating tokens, HCS messages, and staking. Try **"send HBAR"** or **"create a token"**.',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useAIAgent(options: UseAIAgentOptions = {}): UseAIAgentResult {
  const { apiEndpoint = '/api/ai-agent' } = options;
  const { accountId, balance, network, demoMode, isConnected } = useHedera();

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        demoMode
          ? "Hey! I'm the hedera-ui-kit AI agent. Running in demo mode — ask about your balance, try sending HBAR or creating a token to see how it works."
          : "Hey! I'm the hedera-ui-kit AI agent. I can execute Hedera operations from natural language. What would you like to do?",
      timestamp: new Date(),
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hedera action hooks
  const { transfer } = useTransfer();
  const { createToken, error: createTokenError } = useTokenCreate();
  const { burnFungible } = useTokenBurn();
  const { scheduleTransfer } = useScheduledTransaction();
  const { submitMessage: submitHCS } = useHCS();
  const { associate } = useTokenAssociate();

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const full: ChatMessage = { ...msg, id: `${Date.now()}-${Math.random()}`, timestamp: new Date() };
    setMessages((prev) => [...prev, full]);
    return full.id;
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      setError(null);

      addMessage({ role: 'user', content: text });
      setLoading(true);

      // ── Always handle the send-HBAR multi-step flow locally ──────────────────
      // This runs regardless of demo/live mode so Groq can never bypass the
      // collect-amount → collect-address → confirm pattern.
      const lastAsst = lastAssistantMsg(messages);
      const lowerText = text.toLowerCase().trim();
      const inSendFlow =
        lastAsst.includes('__AWAITING_AMOUNT__') ||
        lastAsst.includes('__AWAITING_ADDRESS__');
      const isSendIntent =
        !inSendFlow &&
        (lowerText.includes('send') ||
          lowerText.includes('transfer') ||
          lowerText.includes('отправить') ||
          lowerText.includes('перевест'));

      if (inSendFlow || isSendIntent) {
        await new Promise((r) => setTimeout(r, DEMO_DELAY));
        const demo = getDemoResponse(text, balance, messages);
        addMessage({ role: 'assistant', content: demo.message, action: demo.action });
        setLoading(false);
        return;
      }

      // Demo mode — no real API call for all other queries
      if (demoMode || !apiEndpoint) {
        await new Promise((r) => setTimeout(r, DEMO_DELAY));
        const demo = getDemoResponse(text, balance, messages);
        addMessage({ role: 'assistant', content: demo.message, action: demo.action });
        setLoading(false);
        return;
      }

      // Build history for the API (exclude welcome + system msgs)
      const history = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
      history.push({ role: 'user', content: text });

      try {
        const res = await fetch(apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: history,
            context: { accountId, balance, network, demoMode },
          }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(err.error ?? 'AI request failed');
        }

        const data = await res.json() as { message: string; action?: AIAction };
        addMessage({ role: 'assistant', content: data.message, action: data.action });
      } catch {
        // API unavailable (e.g. GROQ_API_KEY not set) — fall back to scripted
        // responses so action cards still appear and wallet operations still work.
        const demo = getDemoResponse(text, balance, messages);
        addMessage({ role: 'assistant', content: demo.message, action: demo.action });
      } finally {
        setLoading(false);
      }
    },
    [messages, accountId, balance, network, demoMode, apiEndpoint, addMessage]
  );

  const confirmAction = useCallback(
    async (messageId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      if (!msg?.action) return;

      const { type, params } = msg.action;
      setExecuting(true);

      // Remove pending action from the message while executing
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, action: undefined } : m))
      );

      const toastId = toast.loading(`Executing: ${msg.action.description}…`);

      try {
        let txId: string | null = null;
        let tokenId: string | null = null;

        // ── Demo mode: simulate results without a real wallet ──
        if (demoMode) {
          await new Promise((r) => setTimeout(r, 1500));
          const fakeTs = Math.floor(Date.now() / 1000);
          const fakeAcct = `0.0.${Math.floor(Math.random() * 9000000) + 1000000}`;
          if (type === 'create_token') {
            tokenId = `0.0.${Math.floor(Math.random() * 9000000) + 1000000}`;
          } else {
            txId = `${fakeAcct}@${fakeTs}`;
          }
        } else {
          // ── Live mode: require connected wallet ──
          if (!isConnected || !accountId) {
            throw new Error('Wallet not connected. Please connect your wallet first.');
          }

          switch (type) {
            case 'transfer_hbar':
              txId = await transfer(String(params.to), Number(params.amount));
              if (!txId) throw new Error('Transfer failed — check recipient address and balance.');
              break;
            case 'create_token':
              tokenId = await createToken({
                name: String(params.name),
                symbol: String(params.symbol),
                type: (params.type as 'FUNGIBLE' | 'NFT'),
                initialSupply: params.initialSupply != null ? Number(params.initialSupply) : undefined,
                decimals: params.decimals != null ? Number(params.decimals) : undefined,
                maxSupply: params.maxSupply != null ? Number(params.maxSupply) : undefined,
                memo: params.memo != null ? String(params.memo) : undefined,
              });
              if (!tokenId) throw new Error(createTokenError ?? 'Token creation failed — transaction rejected or timed out.');
              break;
            case 'burn_tokens':
              txId = await burnFungible(String(params.tokenId), Number(params.amount));
              if (!txId) throw new Error('Burn failed — verify token ID and supply.');
              break;
            case 'schedule_transfer':
              txId = await scheduleTransfer(
                String(params.to),
                Number(params.amount),
                params.memo ? String(params.memo) : undefined
              );
              if (!txId) throw new Error('Scheduled transfer failed.');
              break;
            case 'submit_hcs_message':
              txId = await submitHCS(String(params.topicId), String(params.message));
              if (!txId) throw new Error('HCS submit failed — verify topic ID.');
              break;
            case 'associate_token':
              txId = await associate(String(params.tokenId));
              if (!txId) throw new Error('Token association failed — verify token ID.');
              break;
          }
        }

        const success = !!(txId ?? tokenId);
        toast.success('✅ Transaction confirmed', { id: toastId });

        // Update the message with result
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, actionResult: { success: true, txId, tokenId } }
              : m
          )
        );

        // Add follow-up confirmation message
        const followUp = txId
          ? `✅ Done! TX ID: \`${txId}\``
          : tokenId
          ? `✅ Token created: \`${tokenId}\``
          : '✅ Action completed successfully.';

        addMessage({ role: 'assistant', content: followUp });
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'Transaction failed';
        toast.error(`❌ ${errMsg}`, { id: toastId });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, actionResult: { success: false, error: errMsg } }
              : m
          )
        );
        addMessage({ role: 'assistant', content: `❌ Error: ${errMsg}` });
      } finally {
        setExecuting(false);
      }
    },
    [messages, demoMode, isConnected, accountId, transfer, createToken, createTokenError, burnFungible, scheduleTransfer, submitHCS, associate, addMessage]
  );

  const cancelAction = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId ? { ...m, action: undefined } : m
      )
    );
    addMessage({ role: 'assistant', content: 'Action cancelled.' });
  }, [addMessage]);

  const clearChat = useCallback(() => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: 'Chat cleared. How can I help?',
      timestamp: new Date(),
    }]);
    setError(null);
  }, []);

  return { messages, loading, executing, error, sendMessage, confirmAction, cancelAction, clearChat };
}
